import { isDealerUser, pickUserType } from "./userTypes";
import { resolveMediaUrl } from "./userIdentity";

/** Must match dealers app.info `ATP_DEALERS` + `_localstorage_auth`. */
export const DEALERS_AUTH_STORAGE_KEY = "ATP_DEALERS_localstorage_auth";

function pickAuthImage(source = {}, nested = {}) {
  const raw =
    source.image ||
    source.image_url ||
    source.profile_image ||
    source.profile_image_url ||
    source.avatar ||
    source.avatar_url ||
    nested.image ||
    nested.image_url ||
    nested.profile_image ||
    nested.profile_image_url ||
    nested.avatar ||
    nested.avatar_url ||
    "";
  return resolveMediaUrl(raw) || "";
}

/**
 * Flatten a login API payload into the shape dealers auth reducer stores.
 */
export function buildDealersAuthBlob(payload = {}) {
  const user = payload.user || {};
  const userType = pickUserType(payload, user);
  return {
    ...payload,
    ...user,
    firstname:
      user.firstname ||
      user.first_name ||
      payload.firstname ||
      payload.first_name ||
      "",
    lastname:
      user.lastname ||
      user.last_name ||
      payload.lastname ||
      payload.last_name ||
      "",
    username: user.username || payload.username || "",
    email: user.email || payload.email || "",
    id: user.id || payload.id || "",
    image: pickAuthImage(payload, user),
    token: payload.token || "",
    expiry: payload.expiry || "",
    type: userType,
    loging: false,
    isLogedin: Boolean(payload.token),
  };
}

function writeDealersAuthBlob(blob) {
  if (!blob?.token) return;
  window.localStorage.setItem(
    DEALERS_AUTH_STORAGE_KEY,
    JSON.stringify(blob)
  );
}

/** After home login as a dealer, mirror the session into dealers localStorage. */
export function syncDealersSessionFromHomeLogin(payload) {
  if (typeof window === "undefined" || !payload) return;
  if (!isDealerUser(payload) && !isDealerUser(payload.user || {})) return;
  try {
    writeDealersAuthBlob(buildDealersAuthBlob(payload));
  } catch (e) {
    /* ignore storage errors */
  }
}

/**
 * Keep dealers portal session in sync when profile/avatar updates on the main site.
 * Merges into any existing dealers auth blob so dashboard UI sees the new image.
 */
export function syncDealersSessionFromAuthState(authState) {
  if (typeof window === "undefined" || !authState) return;
  if (!isDealerUser(authState) && !isDealerUser(authState.user || {})) return;
  if (!authState.token) return;
  try {
    const existing = readAuthJson(DEALERS_AUTH_STORAGE_KEY) || {};
    const merged = buildDealersAuthBlob({
      ...existing,
      ...authState,
      user: {
        ...(existing.user || {}),
        ...(authState.user || {}),
      },
      token: authState.token || existing.token || "",
      expiry: authState.expiry || existing.expiry || "",
    });
    // Prefer a non-empty image from either side so we never wipe a good avatar.
    if (!merged.image && (existing.image || authState.image)) {
      merged.image = pickAuthImage(authState, existing) || existing.image || "";
    }
    writeDealersAuthBlob(merged);
  } catch (e) {
    /* ignore storage errors */
  }
}

/** Clear dealers session (e.g. when a synced dealer logs out on home). */
export function clearSyncedDealersSession() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(DEALERS_AUTH_STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
}

const CLIENT_AUTH_STORAGE_KEY = "ATP_localstorage_auth";

function readAuthJson(key) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (e) {
    return null;
  }
}

/** True when a dealer session exists (dealers store or home login sync). */
export function hasDealerPortalAccess(authFromRedux) {
  if (authFromRedux?.token && isDealerUser(authFromRedux)) return true;
  const dealers = readAuthJson(DEALERS_AUTH_STORAGE_KEY);
  if (dealers?.token && isDealerUser(dealers)) return true;
  const client = readAuthJson(CLIENT_AUTH_STORAGE_KEY);
  if (client?.token && isDealerUser(client)) return true;
  return false;
}
